import React, { useEffect, useRef, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// GoogleLoginButton
//
// Utilise l'API Google Identity Services (GSI) pour la connexion OAuth.
// Le CLIENT_ID Google doit être configuré dans .env :
//   REACT_APP_GOOGLE_CLIENT_ID=xxxx.apps.googleusercontent.com
//
// Pour tester sans vrai compte Google, un bouton "simuler" s'affiche
// automatiquement quand le client ID n'est pas configuré.
// ─────────────────────────────────────────────────────────────────────────────

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function GoogleLoginButton({ onSuccess, onError, label = "Se connecter avec Google" }) {
  const btnRef = useRef(null);
  const [gsiReady, setGsiReady] = useState(false);
  const [loading, setLoading] = useState(false);

  // Charger le script Google GSI une seule fois
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // Mode démo sans vrai client ID

    if (window.google?.accounts?.id) {
      initGoogle();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => initGoogle();
    document.head.appendChild(script);

    return () => {
      // Nettoyage
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initGoogle = () => {
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    if (btnRef.current) {
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: "outline",
        size: "large",
        width: btnRef.current.offsetWidth || 360,
        text: "signin_with",
        locale: "fr",
        shape: "rectangular",
      });
    }
    setGsiReady(true);
  };

  const handleCredentialResponse = (response) => {
    try {
      // On envoie le JWT brut au backend qui le vérifie côté serveur
      onSuccess({ credential: response.credential });
    } catch (err) {
      console.error("Erreur Google:", err);
      if (onError) onError("Erreur lors de la connexion Google");
    }
  };

  // ─── Mode démo (pas de GOOGLE_CLIENT_ID configuré) ──────────────────────────
  const simulateGoogle = () => {
    setLoading(true);
    setTimeout(() => {
      // En mode démo, on ne peut pas générer un vrai JWT Google
      // On signale à l'utilisateur que le Client ID n'est pas configuré
      if (onError) onError("Google Client ID non configuré. Ajoutez REACT_APP_GOOGLE_CLIENT_ID dans votre .env");
      setLoading(false);
    }, 300);
  };

  // Si pas de Client ID → bouton de simulation pour développement
  if (!GOOGLE_CLIENT_ID) {
    return (
      <button
        onClick={simulateGoogle}
        disabled={loading}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          padding: "12px 16px",
          backgroundColor: "white",
          border: "1.5px solid #dadce0",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#3c4043",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "background 0.15s, box-shadow 0.15s",
          boxShadow: loading ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
          fontFamily: "inherit",
          opacity: loading ? 0.7 : 1,
        }}
        onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)"; }}
      >
        {loading ? (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4285F4" strokeWidth="2"
              style={{ animation: "spin 1s linear infinite" }}>
              <circle cx="12" cy="12" r="10" strokeOpacity="0.3"/>
              <path d="M12 2a10 10 0 0110 10"/>
            </svg>
            Connexion en cours...
          </>
        ) : (
          <>
            {/* Icône Google */}
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {label}
          </>
        )}
      </button>
    );
  }

  // Bouton Google officiel rendu par GSI
  return (
    <div
      ref={btnRef}
      style={{
        width: "100%",
        minHeight: "44px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: gsiReady ? 1 : 0.5,
        transition: "opacity 0.3s"
      }}
    />
  );
}

export default GoogleLoginButton;